import type { PreferenciasMenu } from '../api/operaciones';
import { CLAVE_INICIO, agrupar, type EntradaMenu, type GrupoMenu, type Menu } from './navegacion';

/**
 * «Personalizar menú» (G129, ADR-0051; componentes.md §1g): lo que la persona cambia de su menú en este negocio, sin
 * nada visual. Parte siempre del menú que manda el servidor (ya con lo guardado aplicado) y devuelve los cambios para
 * `updateMyNavigationPreferences`; el servidor los aplica y el panel vuelve a pedir el menú. Reglas del contrato:
 *
 * - Ordenar es **dentro de cada categoría**, nunca de una a otra (`group_order`).
 * - Quitar del menú lleva la entrada a «Más» (`in_more`); **Inicio no se quita**.
 * - La barra del móvil tiene de 1 a 4 entradas del menú principal (`mobile_bar`), contratadas.
 * - «Restablecer» manda `null` en los campos de su pantalla: ordenador (`group_order`, `in_more`) y móvil (`mobile_bar`)
 *   no se borran el uno al otro.
 */

/** Las categorías para ordenar: todas las entradas del menú de cada una (también las quitadas), en el orden de la API. */
export function categoriasParaOrdenar(menu: Pick<Menu, 'entradas'>): GrupoMenu[] {
    return agrupar(menu.entradas);
}

/** La entrada se puede quitar del menú (Inicio no). */
export function sePuedeQuitar(entrada: Pick<EntradaMenu, 'clave'>): boolean {
    return entrada.clave !== CLAVE_INICIO;
}

/**
 * Mueve una entrada dentro de su categoría `pasos` posiciones (negativo, hacia arriba) y devuelve el `group_order`
 * completo (el campo se guarda entero: todas las categorías con su orden actual). Fuera de los límites, se queda en el
 * borde; si no cambia nada, null.
 */
export function moverEnCategoria(menu: Pick<Menu, 'entradas'>, clave: string, pasos: number): PreferenciasMenu | null {
    const orden: Record<string, string[]> = {};
    let cambia = false;
    for (const grupo of categoriasParaOrdenar(menu)) {
        const claves = grupo.entradas.map((e) => e.clave);
        const desde = claves.indexOf(clave);
        if (desde >= 0) {
            const hasta = Math.max(0, Math.min(claves.length - 1, desde + pasos));
            cambia = desde !== hasta;
            claves.splice(desde, 1);
            claves.splice(hasta, 0, clave);
        }
        orden[grupo.clave] = claves;
    }
    return cambia ? { group_order: orden } : null;
}

/**
 * Quita una entrada del menú (a «Más») o la devuelve. Devuelve el `in_more` nuevo, o null si no cambia nada (Inicio
 * no se quita).
 */
export function cambiarQuitada(menu: Pick<Menu, 'entradas'>, clave: string, quitar: boolean): PreferenciasMenu | null {
    const quitadas = menu.entradas.filter((e) => e.enMas).map((e) => e.clave);
    if (quitar === quitadas.includes(clave) || (quitar && clave === CLAVE_INICIO)) {
        return null;
    }
    return { in_more: quitar ? [...quitadas, clave] : quitadas.filter((c) => c !== clave) };
}

/** Restablecer el menú de ordenador: orden y quitadas de siempre (no toca la barra del móvil). */
export const RESTABLECER_MENU: PreferenciasMenu = { group_order: null, in_more: null };

/** Restablecer la barra del móvil (no toca el orden ni las quitadas de ordenador). */
export const RESTABLECER_BARRA: PreferenciasMenu = { mobile_bar: null };

/** ¿Hay algo guardado que restablecer en el menú de ordenador? */
export function hayMenuGuardado(p: PreferenciasMenu | null): boolean {
    return p !== null && ((p.group_order != null && Object.keys(p.group_order).length > 0) || (p.in_more != null && p.in_more.length > 0));
}

/** ¿Hay una barra del móvil guardada? */
export function hayBarraGuardada(p: PreferenciasMenu | null): boolean {
    return p?.mobile_bar != null;
}

/** Máximo de entradas en la barra del móvil (G97). */
export const MAXIMO_BARRA = 4;

/** Entradas que pueden ir en la barra del móvil: las del menú principal contratadas. */
export function candidatasABarra(menu: Pick<Menu, 'entradas'>): EntradaMenu[] {
    return menu.entradas.filter((e) => !e.noContratada);
}

/** Mueve una entrada de la barra `pasos` posiciones. */
export function moverEnBarra(barra: readonly string[], clave: string, pasos: number): PreferenciasMenu | null {
    const claves = [...barra];
    const desde = claves.indexOf(clave);
    const hasta = Math.max(0, Math.min(claves.length - 1, desde + pasos));
    if (desde < 0 || desde === hasta) {
        return null;
    }
    claves.splice(desde, 1);
    claves.splice(hasta, 0, clave);
    return { mobile_bar: claves };
}

/** Quita una entrada de la barra (pasa a «Más»). La barra no se queda nunca vacía. */
export function quitarDeBarra(barra: readonly string[], clave: string): PreferenciasMenu | null {
    if (!barra.includes(clave) || barra.length <= 1) {
        return null;
    }
    return { mobile_bar: barra.filter((c) => c !== clave) };
}

/** Pone una entrada de «Más» al final de la barra; con 4, apagado. */
export function ponerEnBarra(barra: readonly string[], clave: string): PreferenciasMenu | null {
    if (barra.includes(clave) || barra.length >= MAXIMO_BARRA) {
        return null;
    }
    return { mobile_bar: [...barra, clave] };
}

/** Zona de «Personalizar menú» de ordenador: una categoría (`NavigationGroup`) o «Más» (las quitadas). */
export const ZONA_MAS = 'mas';

/**
 * Suelta una entrada arrastrada en una zona, delante de `antes` (null: al final). En su categoría, la ordena (y, si
 * estaba quitada, la devuelve al menú); en «Más», la quita. A otra categoría no se puede (null). Devuelve los cambios
 * para guardar en una sola petición, o null si no cambia nada.
 */
export function soltarEnZona(menu: Pick<Menu, 'entradas'>, clave: string, zona: string, antes: string | null): PreferenciasMenu | null {
    const entrada = menu.entradas.find((e) => e.clave === clave);
    if (entrada === undefined) {
        return null;
    }
    if (zona === ZONA_MAS) {
        return cambiarQuitada(menu, clave, true);
    }
    if (zona !== entrada.grupo) {
        return null;
    }
    const cambios: PreferenciasMenu = {};
    const orden: Record<string, string[]> = {};
    let cambia = false;
    for (const grupo of categoriasParaOrdenar(menu)) {
        let claves = grupo.entradas.map((e) => e.clave);
        if (grupo.clave === zona) {
            const antesDe = claves.join();
            claves = claves.filter((c) => c !== clave);
            const i = antes === null ? -1 : claves.indexOf(antes);
            claves.splice(i < 0 ? claves.length : i, 0, clave);
            cambia = antesDe !== claves.join();
        }
        orden[grupo.clave] = claves;
    }
    if (cambia) {
        cambios.group_order = orden;
    }
    const devolver = cambiarQuitada(menu, clave, false);
    if (devolver !== null) {
        cambios.in_more = devolver.in_more ?? null;
    }
    return Object.keys(cambios).length === 0 ? null : cambios;
}

/** Zonas de la hoja del móvil: la barra y «Más». */
export type ZonaBarra = 'barra' | 'mas';

/**
 * Suelta una entrada en la barra del móvil (delante de `antes`; null: al final) o en «Más». Respeta de 1 a 4 en la
 * barra. Devuelve el `mobile_bar` nuevo, o null si no cambia nada.
 */
export function soltarEnBarra(barra: readonly string[], clave: string, zona: ZonaBarra, antes: string | null): PreferenciasMenu | null {
    if (zona === 'mas') {
        return quitarDeBarra(barra, clave);
    }
    const sin = barra.filter((c) => c !== clave);
    if (sin.length >= MAXIMO_BARRA) {
        return null;
    }
    const i = antes === null ? -1 : sin.indexOf(antes);
    sin.splice(i < 0 ? sin.length : i, 0, clave);
    return sin.join() === barra.join() ? null : { mobile_bar: sin };
}
